/**
 * Frontend helper for consistent delivery type display
 * Single source of truth for delivery type rendering across all components
 */

/**
 * Compute delivery display information from order data
 * @param {Object} order - Order object from API
 * @returns {Object} - { label: string, icon: string }
 */
export const computeDeliveryDisplay = (order) => {
  // Single source of truth: delivery_type enum (HOME/HUB) 
  if (order.delivery_type === 'HUB') {
    return {
      label: 'Hub Pickup',
      icon: '🏣'
    };
  }
  
  if (order.delivery_type === 'HOME') {
    return {
      label: 'Home Delivery', 
      icon: '🏠'
    };
  }
  
  // Fallback: the serializer's display string (backwards compatibility).
  // #184: this read only `delivery_type_label`, while requiresHubSelection
  // below reads `delivery_type_display` — two names for the same idea, and
  // different serializers emit different ones. Accept both.
  const label = order.delivery_type_label || order.delivery_type_display;
  if (label) {
    return {
      label,
      icon: label.includes('Hub') ? '🏣' : '🏠'
    };
  }

  // Last resort: is_residential. Order.delivery_type is non-nullable with a
  // HOME default so this should be unreachable, but defaulting a hub order to
  // "Home Delivery" is the costlier way to be wrong — it tells a customer to
  // wait at home for a parcel they must collect.
  if (order.is_residential === false) {
    return {
      label: 'Hub Pickup',
      icon: '🏣'
    };
  }

  return {
    label: 'Home Delivery',
    icon: '🏠'
  };
};

/**
 * Get delivery type for API requests (normalized to backend API enum values)
 * @param {string} deliveryType - Frontend delivery type selection
 * @returns {string} - Backend API delivery type enum (residential/hub_pickup)
 */
export const normalizeDeliveryType = (deliveryType) => {
  if (typeof deliveryType !== 'string') {
    return 'residential'; // Default
  }
  
  const normalized = deliveryType.toLowerCase().replace(/[_\s]/g, '');
  
  // Handle various frontend formats and map to backend API enums
  if (normalized === 'hubpickup' || normalized === 'hub' || deliveryType === 'HUB') {
    return 'hub_pickup';
  }
  
  if (normalized === 'homedelivery' || normalized === 'home' || normalized === 'residential' || deliveryType === 'HOME') {
    return 'residential';
  }
  
  // Default to residential for unknown values
  return 'residential';
};

/**
 * Check if order requires hub selection in UI
 * @param {Object} order - Order object from API  
 * @returns {boolean}
 */
export const requiresHubSelection = (order) => {
  return order.delivery_type === 'HUB' || order.delivery_type_display === 'Hub Pickup';
};

/**
 * Get hub address display string
 * @param {Object} order - Order object with hub_address field
 * @returns {string|null} - Formatted hub address or null
 */
export const getHubAddressDisplay = (order) => {
  if (!requiresHubSelection(order)) {
    return null;
  }
  
  const hubAddress = order.hub_address;
  if (!hubAddress) {
    return null;
  }
  
  // Format hub address for display
  const parts = [];
  
  if (hubAddress.hub_name) {
    parts.push(hubAddress.hub_name);
  }
  
  if (hubAddress.street1) {
    parts.push(hubAddress.street1);
  }
  
  if (hubAddress.city && hubAddress.state) {
    parts.push(`${hubAddress.city}, ${hubAddress.state}`);
  }
  
  return parts.join(', ') || null;
};

/**
 * Validate payload for delivery type consistency
 * Ensures hub_id is present when delivery_type is HUB
 * @param {Object} payload - API payload to validate
 * @returns {Object} - Validated payload with corrections if needed
 */
export const validateDeliveryTypePayload = (payload) => {
  const validated = { ...payload };
  
  if (validated.delivery_type === 'HUB') {
    if (!validated.hub_id) {
      console.warn('⚠️ HUB delivery type requires hub_id but none provided');
      // Don't auto-correct, let backend handle the validation error
    }
  } else if (validated.delivery_type === 'HOME') {
    // For HOME delivery, hub_id should not be present
    if (validated.hub_id) {
      console.debug('🧹 Removing hub_id for HOME delivery type');
      delete validated.hub_id;
    }
  }
  
  return validated;
};